
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CachedImage {
  query: string;
  url: string;
  description?: string;
  photographer?: string;
  cachedAt: number;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_KEY = 'game_image_cache';

export const useImageCache = () => {
  const [imageCache, setImageCache] = useState<Record<string, CachedImage>>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Load cache from localStorage
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached) as Record<string, CachedImage>;
        // Filter out expired entries
        const validCache: Record<string, CachedImage> = {};
        
        Object.entries(parsedCache).forEach(([key, entry]) => {
          if (entry && typeof entry === 'object' && 'cachedAt' in entry) {
            if (Date.now() - entry.cachedAt < CACHE_DURATION) {
              validCache[key] = entry as CachedImage;
            }
          }
        });
        
        setImageCache(validCache);
      } catch (error) {
        console.error('Error loading image cache:', error);
      }
    }
  }, []);

  const saveCache = (cache: Record<string, CachedImage>) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving image cache:', error);
    }
  };

  const fetchImage = async (query: string): Promise<string | null> => {
    // Check cache first
    if (imageCache[query] && Date.now() - imageCache[query].cachedAt < CACHE_DURATION) {
      return imageCache[query].url;
    }

    // Check if already loading
    if (loadingImages.has(query)) {
      return null;
    }

    setLoadingImages(prev => new Set([...prev, query]));

    try {
      const { data, error } = await supabase.functions.invoke('fetch-images', {
        body: { query, count: 1 }
      });

      if (error) throw error;

      if (data?.images && data.images.length > 0) {
        const image = data.images[0];
        const cachedImage: CachedImage = {
          query,
          url: image.url,
          description: image.description,
          photographer: image.photographer,
          cachedAt: Date.now()
        };

        const newCache = { ...imageCache, [query]: cachedImage };
        setImageCache(newCache);
        saveCache(newCache);
        
        return image.url;
      }
    } catch (error) {
      console.error('Error fetching image for query:', query, error);
    } finally {
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(query);
        return newSet;
      });
    }

    return null;
  };

  return {
    fetchImage,
    isLoading: (query: string) => loadingImages.has(query),
    getCachedImage: (query: string) => imageCache[query]
  };
};
